import React, { useMemo } from 'react';
import { useField } from 'formik';
import { Q1Value } from 'models/documents';
import { DataGridComplete, DataGridRow } from 'components/DataGrid';
import TextInput from 'components/Input/TextInput';
import TooltipControlled from 'components/Tooltip/tooltipControlled';
import { BoundingBox } from 'models/response';
import { breakpointsPalette } from 'utils/styleConstants';
import useMediaWidth from 'hooks/useMediaWidth';

export interface CellTextInput {
  name: string;
  focus: boolean;
  onFocus: (cell?: { pageNumber: number; boundingBox: BoundingBox; content: string }) => void;
  row: DataGridRow;
  code: number;
  disabled?: boolean;
}

const CellTextInput = ({ name, focus, onFocus, row, code, disabled }: CellTextInput) => {
  const [field, , helpers] = useField<Q1Value>(name);
  // Formatting and updating the rows state on inputs change
  const onFieldChange = (value: string, rowId: string, fieldId: string | number) => {
    const body: DataGridComplete[] | undefined = field.value.body?.map((elem: DataGridComplete) => {
      if (elem.id === rowId) {
        return {
          ...elem,
          [fieldId]: {
            content: value,
            pageNumber: -1,
            boundingBox: {
              x1: -1,
              y1: -1,
              x2: -1,
              y2: -1,
            },
          },
        };
      }
      return elem;
    });
    if (body) helpers.setValue({ ...field.value, body });
  };

  let value = '';
  const fieldValue = field.value.body?.find(({ id }) => id === row.id);
  if (fieldValue) {
    value = fieldValue[code]?.content || '';
  }

  // Function to calculate the width of the text
  function getTextWidth(text: string, font: string): number {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 0;
    context.font = font;
    return context.measureText(text).width;
  }

  // Font used in the input (update if necessary)
  const inputFont = '13px Roboto';

  const isOverflowing = useMemo(() => {
    if (!value) return false;
    return getTextWidth(value, inputFont) > 400;
  }, [value]);

  const width = useMediaWidth();

  // Show the tooltip only if there is text and if the text is cut off
  const showTooltip = !!value && isOverflowing;

  return (
    <>
      {showTooltip ? (
        <TooltipControlled
          content={value}
          trigger="hover"
          placement={width > Number(breakpointsPalette.m.replace('px', '')) ? 'bottom' : 'right'}
          maxWidth="380px"
        >
          <TextInput
            id={`cell-text-input-${row.id}`}
            value={value}
            focus={focus}
            onFocus={() => onFocus(fieldValue ? fieldValue[code] : null)}
            type={'text'}
            onChange={(e: React.FocusEvent<HTMLInputElement>) => onFieldChange(e.target.value, row.id, code)}
            noBorder={true}
            fullWidth={true}
            disabled={disabled}
          />
        </TooltipControlled>
      ) : (
        <TextInput
          id={`cell-text-input-${row.id}`}
          value={value}
          focus={focus}
          onFocus={() => onFocus(fieldValue ? fieldValue[code] : null)}
          type={'text'}
          onChange={(e: React.FocusEvent<HTMLInputElement>) => onFieldChange(e.target.value, row.id, code)}
          noBorder={true}
          fullWidth={true}
          disabled={disabled}
        />
      )}
    </>
  );
};

export default CellTextInput;
